import React, { useContext, useRef } from "react";
import debounce from "lodash/debounce";

import { ActionDropdown } from "../ActionDropdown/ActionDropdown";
import {
  roles,
  statusPrestationsValues,
  subRoles,
  subStatus,
} from "../../../utils/constants";
import { ActionRoleSpecific, onlyRoleMapping } from "../../../engine/AuthRoute";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, notification } from "antd";
import { RiBillLine, RiDeleteBinLine } from "react-icons/ri";
import { MdPublishedWithChanges } from "react-icons/md";
import { FcCancel } from "react-icons/fc";
import {
  dangerousUpdate,
  deletePrestation,
  duplicatePrestation,
  getPrestation,
  setPropertiesPrestation,
  updatePrestation,
} from "../../../pages/Prestations/Slices/Prestation.slice";
import {
  confirmCancelPrestation,
  confirmDeletePrestation,
  confirmDuplicatePrestation,
  modalsByContext,
} from "../Modals/modals";
import DownloadICS from "../DownloadICS/DownloadICS";
import dayjs from "../../../utils/dayjs";
import { UserContext } from "../../../engine/context/User.context";
import { BiDuplicate } from "react-icons/bi";
import { PrestationCancel } from "../Modals/PrestationCancel";
import { ModalContext } from "../../../engine/context/Modal.provider";
import { TbPlayerTrackNextFilled } from "react-icons/tb";
import routes from "../../../engine/routes";
import {
  FaCheck,
  FaCheckDouble,
  FaRegComments,
  FaRegFilePdf,
} from "react-icons/fa";
import useDocumentCreator from "../../../engine/hooks/useDocumentCreator";
import { billPdfFileName } from "../../../utils/billPdfFileName";
import { http } from "../../../engine/api/http";
import { GrUserAdmin } from "react-icons/gr";
import { AiOutlineGateway } from "react-icons/ai";
import { usePrestationDisabler } from "../../../engine/hooks/usePrestationDisabler";

export const PrestationAction = ({}) => {
  const { prestation, loading } = useSelector(state => state.prestation);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const context = useContext(ModalContext);

  const [get, createAndGet, isLoading, setUrl, doc] = useDocumentCreator();
  const [disabled] = usePrestationDisabler();

  // Prevent rapid multi-clicks across re-renders using stable keys
  const clickGuards = useRef({});
  const preventMultiClick = (key, fn) => {
    if (!clickGuards.current[key]) {
      clickGuards.current[key] = debounce(fn, 800, {
        leading: true,
        trailing: false,
      });
    }
    return clickGuards.current[key];
  };

  const getPdf = (withHeader = true) => {
    createAndGet(
      { bills: [prestation.bill._id], withHeader },
      `bills/${prestation.bill._id}/pdf`,
      true,
      true,
      () => {},
      billPdfFileName(prestation.bill),
    );
  };

  const { status, _id } = prestation;

  const deleteDraftPrestation = () => {
    confirmDeletePrestation({
      onOk: () => {
        dispatch(deletePrestation());
      },
    });
  };

  const isApplicantArchived = () =>
    prestation.applicant?.status === "archived";

  const duplicatePrestationClick = () => {
    if (isApplicantArchived()) {
      notification.error({
        message:
          "Le demandeur de cette prestation est archivé. Il n'est plus possible de dupliquer cette prestation.",
      });
      return;
    }

    confirmDuplicatePrestation({
      onOk: () => {
        dispatch(duplicatePrestation());
      },
    });
  };

  const goToBill = () => {
    navigate(routes.billing(prestation.bill._id).edition.path);
  };

  let isCanceledClient =
    [statusPrestationsValues.cancelClient].includes(prestation.status) ||
    [subStatus.institution].includes(prestation.subStatus);

  let isFinish = [
    statusPrestationsValues.finish,
    statusPrestationsValues.finish_notsigned,
    statusPrestationsValues.finish_permanance,
  ].includes(prestation.status);

  let isCancelInterprete =
    [statusPrestationsValues.cancelInterprete].includes(prestation.status) ||
    [subStatus.interprete].includes(prestation.subStatus);

  const blue = "#1a61c4";
  const size = 18;

  // Function to check if user can create a follow-up
  const canCreateFollow = () => {
    const { status } = prestation;

    // Check if status allows creating follow
    const statusAllowed =
      status !== statusPrestationsValues.draft &&
      status !== statusPrestationsValues.waiting &&
      status !== statusPrestationsValues.cancel;

    if (!statusAllowed) return false;

    // Check role permissions and restrictions
    const hasBasicPermission = onlyRoleMapping([
      roles.admin,
      roles.permanance,
      roles.interprete,
    ]);
    if (!hasBasicPermission) return false;

    // Check interpreter restrictions
    if (
      user.role === roles.interprete &&
      user.interpreteData?.restrictionFollowPrestation === true
    ) {
      return false;
    }

    // Check institution restrictions
    if (
      user.role === roles.institution &&
      user.institution?.restrictionFollowPrestation === true
    ) {
      return false;
    }

    return true;
  };

  return (
    prestation._id !== undefined && (
      <>
        <ActionDropdown loading={isLoading || loading}>
          <ActionRoleSpecific
            only={[
              roles.admin,
              roles.permanance,
              subRoles.institutionAdmin,
              subRoles.institutionDemander,
            ]}
            key={"Supprimer la prestation"}
          >
            {status === statusPrestationsValues.draft &&
            onlyRoleMapping([
              roles.admin,
              roles.permanance,
              subRoles.institutionAdmin,
              subRoles.institutionDemander,
            ]) ? (
              <a
                onClick={preventMultiClick("deletePres", deleteDraftPrestation)}
                key={"deletePres"}
              >
                <span style={{ marginLeft: 5, marginRight: 10 }}>
                  <RiDeleteBinLine size={size} color={"red"} />
                </span>
                Supprimer la prestation
              </a>
            ) : null}
          </ActionRoleSpecific>

          <ActionRoleSpecific
            only={[
              roles.permanance,
              subRoles.institutionAdmin,
              subRoles.institutionDemander,
            ]}
            key={"Modifier la prestation"}
          >
            {(status === statusPrestationsValues.planned ||
              status === statusPrestationsValues.waiting) &&
            user?.institution?.readOnlyPrestation !== true &&
            onlyRoleMapping([
              roles.permanance,
              subRoles.institutionAdmin,
              subRoles.institutionDemander,
            ]) ? (
              <a
                key={"updatePresta"}
                onClick={preventMultiClick("updatePresta1", () => {
                  dispatch(dangerousUpdate());
                })}
              >
                <span style={{ marginLeft: 5, marginRight: 10 }}>
                  <MdPublishedWithChanges size={size} color={blue} />
                </span>
                Modifier la prestation
              </a>
            ) : null}
          </ActionRoleSpecific>

          <ActionRoleSpecific
            only={[roles.admin]}
            key={"Modifier la prestation admin"}
          >
            {onlyRoleMapping([roles.admin]) ? (
              <a
                key={"updatePresta"}
                onClick={preventMultiClick("updatePrestaAdmin", () => {
                  dispatch(dangerousUpdate());
                })}
              >
                <span style={{ marginLeft: 5, marginRight: 10 }}>
                  <MdPublishedWithChanges size={size} color={blue} />
                </span>
                Modifier la prestation
              </a>
            ) : null}
          </ActionRoleSpecific>

          {onlyRoleMapping([
            roles.admin,
            roles.permanance,
            subRoles.institutionAdmin,
            subRoles.institutionDemander,
          ]) && user?.institution?.readOnlyPrestation !== true ? (
            <a
              onClick={preventMultiClick(
                "duplicatePresta",
                duplicatePrestationClick,
              )}
              key={"Annuler"}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <BiDuplicate color={blue} size={size} />
              </span>
              Dupliquer la prestation
            </a>
          ) : null}

          {_id !== undefined &&
          onlyRoleMapping([roles.admin, roles.permanance]) ? (
            <a
              disabled={disabled}
              onClick={preventMultiClick("editComment", async () => {
                !disabled &&
                  context.openModal(modalsByContext.PrestationEditAdmin);
              })}
              className={disabled ? "disabled" : ""}
              key={modalsByContext.PrestationEditAdmin}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <FaRegComments color={blue} size={size} />
              </span>
              Modifier commentaire
            </a>
          ) : null}

          {_id !== undefined &&
          onlyRoleMapping([roles.admin, roles.permanance]) ? (
            <a
              onClick={preventMultiClick("control1", async () => {
                context.openModal(modalsByContext.PrestationControl, {
                  control: 1,
                });
              })}
              key={modalsByContext.PrestationControl}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <FaCheck color={"green"} size={size} />
              </span>
              1er Contrôle
            </a>
          ) : null}

          {_id !== undefined &&
          onlyRoleMapping([roles.admin, roles.permanance]) ? (
            <a
              onClick={preventMultiClick("control2", async () => {
                context.openModal(modalsByContext.PrestationControl, {
                  control: 2,
                });
              })}
              key={modalsByContext.PrestationControl}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <FaCheckDouble color={"green"} size={size} />
              </span>
              2ème Contrôle
            </a>
          ) : null}

          {[
            statusPrestationsValues.finish,
            statusPrestationsValues.waiting,
            statusPrestationsValues.planned,
            statusPrestationsValues.finish_notsigned,
            statusPrestationsValues.finish_permanance,
            statusPrestationsValues.cancel,
          ].includes(status) &&
          user?.institution?.readOnlyPrestation !== true &&
          onlyRoleMapping([
            roles.admin,
            roles.permanance,
            subRoles.institutionAdmin,
            subRoles.institutionDemander,
          ]) ? (
            <a
              disabled={disabled}
              className={disabled ? "disabled" : ""}
              onClick={preventMultiClick("cancelPresta", async () => {
                // const confirmed = await modal.confirm(config);
                !disabled &&
                  context.openModal(modalsByContext.PrestationCancel);
              })}
              key={modalsByContext.PrestationCancel}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <FcCancel color={"red"} size={size} />
              </span>
              Annuler la prestation
            </a>
          ) : null}

          {[
            statusPrestationsValues.waiting,
            statusPrestationsValues.planned,
          ].includes(status) &&
          onlyRoleMapping([
            //subRoles.institutionAdmin,
            //  subRoles.institutionDemander,
          ]) ? (
            <a
              onClick={preventMultiClick("cancelPresta2", async () => {
                context.openModal(modalsByContext.PrestationCancel);
              })}
              key={modalsByContext.PrestationCancel}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <FcCancel color={"red"} size={size} />
              </span>
              Annuler la prestation
            </a>
          ) : null}

          {onlyRoleMapping([roles.admin, roles.permanance]) &&
          prestation?.bill !== null ? (
            <a
              disabled={disabled}
              className={disabled ? "disabled" : ""}
              onClick={preventMultiClick("goToBill", goToBill)}
              key={"bill"}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <RiBillLine color={"#00377c"} size={size} />
              </span>
              Modifier la facture
            </a>
          ) : null}

          {onlyRoleMapping([roles.admin, roles.permanance]) &&
          prestation?.bill !== undefined &&
          prestation?.bill !== null ? (
            <a
              onClick={preventMultiClick("getPdfHeader", () => getPdf(true))}
              key={"getPdfHeader"}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <FaRegFilePdf color={"black"} size={size} />
              </span>
              Télécharger la facture (avec en-tête)
            </a>
          ) : null}

          {onlyRoleMapping([roles.admin, roles.permanance]) &&
          prestation?.bill !== undefined &&
          prestation?.bill !== null ? (
            <a
              onClick={preventMultiClick("getPdfNoHeader", () => getPdf(false))}
              key={"getPdfNoHeader"}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <FaRegFilePdf color={"black"} size={size} />
              </span>
              Télécharger la facture (sans en-tête)
            </a>
          ) : null}

          {canCreateFollow() ? (
            <a
              onClick={preventMultiClick("createFollow", async () => {
                if (isApplicantArchived()) {
                  notification.error({
                    message:
                      "Le demandeur de cette prestation est archivé. Il n'est plus possible de créer un suivi.",
                  });
                  return;
                }
                context.openModal(modalsByContext.PrestationFollow);
              })}
              key={modalsByContext.PrestationFollow}
            >
              <span style={{ marginLeft: 5, marginRight: 10 }}>
                <TbPlayerTrackNextFilled color={"black"} size={size} />
              </span>
              Créer un suivi
            </a>
          ) : null}
          {prestation._id !== undefined ? <DownloadICS type={"link"} /> : null}
        </ActionDropdown>
      </>
    )
  );
};
